import { BlobReader, ZipReader } from "@zip.js/zip.js";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export type ZipValidationErrorCode = "invalid_zip" | "empty_zip" | "no_images";

export type ZipValidationResult = { ok: true } | { ok: false; code: ZipValidationErrorCode };

const IMAGE_EXTENSIONS = new Set([".png", ".jpg", ".jpeg", ".webp"]);
const IGNORED_NAMES = new Set([".ds_store", "thumbs.db"]);

export const useAdvancedUploadValidation = () => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.advancedUpload" });

	const getExtension = useCallback((name: string): string => {
		const lower = name.toLowerCase();
		const idx = lower.lastIndexOf(".");
		return idx === -1 ? "" : lower.slice(idx);
	}, []);

	const getBaseName = useCallback((name: string): string => {
		const parts = name.split("/");
		const fileName = parts[parts.length - 1];
		const dotIdx = fileName.lastIndexOf(".");
		return dotIdx === -1 ? fileName : fileName.slice(0, dotIdx);
	}, []);

	const validateZipFileList = useCallback(
		(files: string[]): ZipValidationResult => {
			if (!files.length) return { ok: false, code: "empty_zip" };

			const fileNames = files
				.filter((n) => !!n && !n.endsWith("/"))
				// ignore Mac OS folder artifacts and hidden dot-underscore files
				.filter((n) => !n.startsWith("__MACOSX/") && !n.includes("/__MACOSX/"))
				.filter((n) => {
					const base = getBaseName(n).toLowerCase();
					return !IGNORED_NAMES.has(base) && !base.startsWith("._");
				});
			const extensions = fileNames.map((n) => getExtension(n));

			const imageExists = extensions.some((ext) => IMAGE_EXTENSIONS.has(ext));
			if (!imageExists) return { ok: false, code: "no_images" };

			return { ok: true };
		},
		[getBaseName, getExtension],
	);

	const extractZipFileNames = useCallback(async (blob: Blob): Promise<string[]> => {
		try {
			// Supports verifying the zip file by stream (no need to load the entire file into memory)
			const blobReader = new BlobReader(blob);
			const zipReader = new ZipReader(blobReader);

			const entries = await zipReader.getEntries();
			const fileNames: string[] = [];

			for (const entry of entries) {
				if (!entry.directory) {
					fileNames.push(entry.filename);
				}
			}

			await zipReader.close();
			return fileNames;
		} catch (ex) {
			console.error("Exception during loading the file names inside the zip file", ex);
			return [];
		}
	}, []);

	const countZipImages = useCallback(async (blob: Blob): Promise<number> => {
		try {
			const blobReader = new BlobReader(blob);
			const zipReader = new ZipReader(blobReader);
			const entries = await zipReader.getEntries();
			let count = 0;
			for (const entry of entries) {
				if (entry.directory) continue;
				const name = entry.filename || "";
				if (!name || name.endsWith("/") || name.startsWith("__MACOSX/") || name.includes("/__MACOSX/"))
					continue;
				const parts = name.split("/");
				const fileName = parts[parts.length - 1];
				const base = (() => {
					const dotIdx = fileName.lastIndexOf(".");
					return dotIdx === -1 ? fileName : fileName.slice(0, dotIdx);
				})().toLowerCase();
				if (IGNORED_NAMES.has(base) || base.startsWith("._")) continue;
				const lower = fileName.toLowerCase();
				const idx = lower.lastIndexOf(".");
				const ext = idx === -1 ? "" : lower.slice(idx);
				if (IMAGE_EXTENSIONS.has(ext)) count++;
			}
			await zipReader.close();
			return count;
		} catch {
			return 0;
		}
	}, []);

	const getValidationErrorMessage = useCallback(
		(result: ZipValidationResult): string | null => {
			if (result.ok) return null;
			switch (result.code) {
				case "invalid_zip":
					return t("errors.invalidZip");
				case "empty_zip":
					return t("errors.emptyZip");
				case "no_images":
					return t("errors.noImagesInZip");
				default:
					return t("uploadError");
			}
		},
		[t],
	);

	const validateZipBlob = useCallback(
		async (blob: Blob) => {
			const names = await extractZipFileNames(blob);
			if (!names.length) return { ok: false, code: "invalid_zip" } as const;
			return validateZipFileList(names);
		},
		[extractZipFileNames, validateZipFileList],
	);

	return {
		validateZipBlob,
		validateZipFileList,
		countZipImages,
		getValidationErrorMessage,
	};
};
